import React from "react";
import {
  Divider,
  FormControl,
  MenuItem,
  Select,
  TextField,
  IconButton,
  Typography,
  Link
} from "@material-ui/core";
import AddIcon from "@material-ui/icons/Add";
import RemoveIcon from "@material-ui/icons/Remove";
import { makeStyles } from "@material-ui/core/styles";
import _ from "lodash";

import { CUSTOM_DICE_TYPES } from "../settings";

import { InputStepper, SelectLabelInputStepper } from "../../Steppers";

const useStyles = makeStyles(theme => ({
  formControl: {
    margin: theme.spacing(1),
    "& .MuiOutlinedInput-input": {
      padding: theme.spacing(1)
    },
    minWidth: `90%`
  },
  highlightText: {
    margin: `0 ${theme.spacing(1)}`,
    minWidth: `90%`,
    textAlign: `center`
  },
  inputStepper: {
    display: `flex`
  },
  inputStepperIcon: {
    marginRight: theme.spacing(1),
    "&:first-child": {
      marginLeft: theme.spacing(1)
    }
  },
  inputStepperTextField: {
    flexGrow: 1,
    marginRight: theme.spacing(1),
    "& .MuiOutlinedInput-input": {
      padding: theme.spacing(1)
    },
    "& .MuiInputLabel-shrink": {
      transform: `translate(14px, 0px) scale(0.75)`
    },
    display: `flex`,
    fledDirection: `column`,
    justifyContent: `center`
  },
  divider: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2)
  },
  link: {
    color:
      theme.palette.type === "dark"
        ? theme.palette.secondary.main
        : theme.palette.primary.main
  }
}));

const defaultModifier = 0;
const minNumberOfDice = 0;
const maxNumberOfDice = 10;
const minModifier = -50;
const maxModifier = 50;

const defaultCustomDiceToRoll = {
  d4: minNumberOfDice,
  d6: minNumberOfDice,
  d8: minNumberOfDice,
  d10: minNumberOfDice,
  d12: minNumberOfDice,
  d20: minNumberOfDice,
  d100: minNumberOfDice
};

const divStyle = {
  fixed: {
    width: `300px`,
    overflowY: `auto`,
    height: `100%`,
    display: `flex`,
    flexDirection: `column`,
    justifyContent: `flex-start`
  },
  drawer: {
    overflowy: `auto`,
    height: `100vh`,
    width: `300px`,
    maxWidth: `85vw`
  }
};

const defaultProps = {
  options: [
    {
      label: `Default Option`,
      dice: [],
      expectModifider: false,
      drop: `None`,
      dropNumber: 0,
      resultType: `Raw Results`
    }
  ],
  onOptionChange: _.noop,
  selectedOption: 0,
  displayStyle: `fixed`
};

// Use this instead of the key order in customDiceToRoll to prevent reordering of fields
// On iPhone Safari
const dieTypeOrder = [`d4`, `d6`, `d8`, `d10`, `d12`, `d20`, `d100`];
const dicePoolOptions = [`d4`, `d6`, `d8`, `d10`, `d12`];

function DiceBoxControlPanel({
  options,
  diceToRoll,
  dicePoolDieType,
  modifier,
  onOptionChange,
  selectedOption,
  displayStyle
}) {
  const classes = useStyles();
  const currentOption = _.get(options, selectedOption);
  const customDiceToRoll = !_.includes(
    [CUSTOM_DICE_TYPES.MULTIPLE, CUSTOM_DICE_TYPES.ONE],
    currentOption.dice
  )
    ? defaultCustomDiceToRoll
    : _.chain(diceToRoll)
        .groupBy()
        .reduce((acc, diceArray, key) => {
          return {
            ...acc,
            [key]: diceArray.length
          };
        }, defaultCustomDiceToRoll)
        .value();

  const displayOptions = _.map(options, (option, index) => (
    <MenuItem key={option.label} value={index}>
      {option.label}
    </MenuItem>
  ));

  const handleDiceToRollChange = evt => {
    const newOption = evt.target.value;

    const diceToRoll = _.isArray(options[newOption].dice)
      ? options[newOption].dice
      : [];

    onOptionChange({
      selectedOption: newOption,
      diceToRoll,
      defaultModifier
    });
  };

  return (
    <div style={divStyle[displayStyle]}>
      <FormControl className={classes.formControl}>
        <Typography variant="h6" id="roll-type-select-label" gutterBottom>
          Choose Your Weapons
        </Typography>
        <Select
          variant="outlined"
          labelId="roll-type-select-label"
          id="roll-type-select"
          value={selectedOption}
          onChange={handleDiceToRollChange}
        >
          {displayOptions}
        </Select>
      </FormControl>
      {currentOption.dice === CUSTOM_DICE_TYPES.MULTIPLE && (
        <>
          {_.map(dieTypeOrder, type => (
            <InputStepper
              key={type}
              label={type}
              value={customDiceToRoll[type]}
              min={minNumberOfDice}
              max={maxNumberOfDice}
              onChange={numberOfDice => {
                const newCustomDiceToRoll = {
                  ...customDiceToRoll,
                  [type]: numberOfDice
                };
                const diceToRoll = _.reduce(
                  newCustomDiceToRoll,
                  (acc, number, type) => {
                    const diceToAdd = Array.from({ length: number }).map(
                      () => type
                    );
                    return _.concat(acc, diceToAdd);
                  },
                  []
                );

                onOptionChange({ selectedOption, diceToRoll, modifier });
              }}
            />
          ))}
        </>
      )}
      {currentOption.dice === CUSTOM_DICE_TYPES.ONE && (
        <SelectLabelInputStepper
          id="dice-pool-select"
          selectLabel="Die Type"
          selectValue={dicePoolDieType}
          selectOptions={dicePoolOptions}
          value={customDiceToRoll[dicePoolDieType]}
          min={minNumberOfDice}
          max={maxNumberOfDice}
          onSelectChange={newDieType => {
            const newDiceToRoll = _.map(diceToRoll, () => newDieType);
            onOptionChange({
              selectedOption,
              diceToRoll: newDiceToRoll,
              defaultModifier,
              dicePoolDieType: newDieType
            });
          }}
          onChange={numberOfDice => {
            const newCustomDiceToRoll = {
              ...customDiceToRoll,
              [dicePoolDieType]: numberOfDice
            };

            const diceToRoll = _.reduce(
              newCustomDiceToRoll,
              (acc, number, type) => {
                const diceToAdd = Array.from({ length: number }).map(
                  () => type
                );
                return _.concat(acc, diceToAdd);
              },
              []
            );

            onOptionChange({ selectedOption, diceToRoll, dicePoolDieType });
          }}
        />
      )}
      {currentOption.expectModifier && (
        <>
          <div className={classes.inputStepper}>
            <IconButton
              color="primary"
              aria-label="subtract 1"
              className={classes.inputStepperIcon}
              onClick={evt => {
                let newModifier = Math.max(modifier - 1, minModifier);

                const diceToRoll = _.isArray(options[selectedOption].dice)
                  ? options[selectedOption].dice
                  : _.reduce(
                      customDiceToRoll,
                      (acc, number, type) => {
                        const diceToAdd = Array.from({ length: number }).map(
                          () => type
                        );
                        return _.concat(acc, diceToAdd);
                      },
                      []
                    );

                onOptionChange({
                  selectedOption,
                  diceToRoll,
                  modifier: newModifier
                });
              }}
            >
              <RemoveIcon />
            </IconButton>
            <TextField
              id="roll_modifier"
              label={`Modifier`}
              variant="outlined"
              className={classes.inputStepperTextField}
              value={_.toString(modifier)}
              onKeyPress={evt => {
                if (`-` === evt.key) {
                  onOptionChange({
                    selectedOption,
                    diceToRoll,
                    modifier: -modifier
                  });
                  evt.preventDefault();
                }
              }}
              onChange={evt => {
                let newModifier = _.toInteger(evt.target.value);
                if (newModifier < minModifier) newModifier = minModifier;
                if (newModifier > maxModifier) newModifier = maxModifier;

                const diceToRoll = _.isArray(options[selectedOption].dice)
                  ? options[selectedOption].dice
                  : _.reduce(
                      customDiceToRoll,
                      (acc, number, type) => {
                        const diceToAdd = Array.from({ length: number }).map(
                          () => type
                        );
                        return _.concat(acc, diceToAdd);
                      },
                      []
                    );

                onOptionChange({
                  selectedOption,
                  diceToRoll,
                  modifier: newModifier
                });
              }}
            />
            <IconButton
              color="primary"
              aria-label="add 1"
              className={classes.inputStepperIcon}
              onClick={evt => {
                let newModifier = Math.min(modifier + 1, maxModifier);

                const diceToRoll = _.isArray(options[selectedOption].dice)
                  ? options[selectedOption].dice
                  : _.reduce(
                      customDiceToRoll,
                      (acc, number, type) => {
                        const diceToAdd = Array.from({ length: number }).map(
                          () => type
                        );
                        return _.concat(acc, diceToAdd);
                      },
                      []
                    );

                onOptionChange({
                  selectedOption,
                  diceToRoll,
                  modifier: newModifier
                });
              }}
            >
              <AddIcon />
            </IconButton>
          </div>
          <div className={classes.highlightText}>
            <Typography variant="body1" color="secondary">
              Modifiers Range from &ndash;{-minModifier} to {maxModifier}
            </Typography>
          </div>
        </>
      )}
      <Divider className={classes.divider} variant="middle" />
      <div className={classes.formControl}>
        Dice moving too slow? Try{" "}
        <Link
          className={classes.link}
          href="https://www.computerhope.com/issues/ch002154.htm"
          rel="noopener noreferrer"
          target="_blank"
        >
          enabling hardware acceleration
        </Link>
        .
      </div>
    </div>
  );
}

DiceBoxControlPanel.defaultProps = defaultProps;

export default DiceBoxControlPanel;
